/**
 * TournamentListModal - List and manage saved tournaments
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import firebaseSyncService from '../../services/firebaseSync';
import ConfirmModal from './ConfirmModal';
import './TournamentListModal.css';

const TournamentListModal = ({ isOpen, onClose, onLoad }) => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showLoadConfirm, setShowLoadConfirm] = useState(false);
  const [pendingLoadTournament, setPendingLoadTournament] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteTournament, setPendingDeleteTournament] = useState(null);

  const { getCurrentUser } = useAuth();
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    if (isOpen) {
      loadTournaments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadTournaments = async () => {
    const user = getCurrentUser();
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const list = await firebaseSyncService.listUserTournaments(user.email);
      setTournaments(list);
    } catch (err) {
      const errorMsg = 'Erreur lors du chargement de la liste';
      setError(errorMsg);
      showError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadClick = (tournament) => {
    setPendingLoadTournament(tournament);
    setShowLoadConfirm(true);
  };

  const handleLoadTournament = async (tournamentId) => {
    const user = getCurrentUser();
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      const data = await firebaseSyncService.loadTournament(tournamentId, user.email);
      if (data && onLoad) {
        showSuccess(`Tournoi "${data.state?.config?.tournamentName || 'Sans nom'}" chargé`);
        onLoad(data);
        onClose();
      }
    } catch (err) {
      const errorMsg = 'Erreur lors du chargement du tournoi';
      setError(errorMsg);
      showError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (tournament) => {
    setPendingDeleteTournament(tournament);
    setShowDeleteConfirm(true);
  };

  const handleDeleteTournament = async (tournamentId) => {
    const user = getCurrentUser();
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      await firebaseSyncService.deleteTournament(tournamentId, user.email);
      showSuccess('Tournoi supprimé');
      await loadTournaments(); // Refresh list
    } catch (err) {
      const errorMsg = 'Erreur lors de la suppression';
      setError(errorMsg);
      showError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateTournament = async (tournamentId) => {
    const user = getCurrentUser();
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      await firebaseSyncService.duplicateTournament(tournamentId, user.email);
      showSuccess('Tournoi dupliqué');
      await loadTournaments(); // Refresh list
    } catch (err) {
      const errorMsg = 'Erreur lors de la duplication';
      setError(errorMsg);
      showError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartRename = (tournament) => {
    setEditingId(tournament.id);
    setEditingName(tournament.state?.config?.tournamentName || '');
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleSaveRename = async (tournamentId) => {
    const user = getCurrentUser();
    if (!user || !editingName.trim()) return;

    setLoading(true);
    setError('');

    try {
      await firebaseSyncService.renameTournament(tournamentId, editingName, user.email);
      showSuccess('Tournoi renommé');
      setEditingId(null);
      setEditingName('');
      await loadTournaments(); // Refresh list
    } catch (err) {
      const errorMsg = 'Erreur lors du renommage';
      setError(errorMsg);
      showError(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return 'Date inconnue';
    const date = timestamp.toDate();
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block tournament-list-modal"
        tabIndex="-1"
        role="dialog"
        style={{ zIndex: 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-folder me-2"></i>
                Mes tournois
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={loading}
              ></button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="tournament-error">{error}</div>
              )}

              {loading && tournaments.length === 0 && (
                <div className="tournament-loading">
                  <div className="spinner-border mb-3" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p>Chargement des tournois...</p>
                </div>
              )}

              {!loading && tournaments.length === 0 && (
                <div className="tournament-empty">
                  <h6>
                    <i className="bi bi-info-circle me-2"></i>
                    Aucun tournoi sauvegardé
                  </h6>
                  <p className="mb-0">
                    Créez votre premier tournoi pour le voir apparaître ici.
                  </p>
                </div>
              )}

              {tournaments.length > 0 && (
                <div>
                  {tournaments.map((tournament) => (
                    <div key={tournament.id} className="tournament-item">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1 me-3">
                          {editingId === tournament.id ? (
                            <div className="tournament-rename-group">
                              <input
                                type="text"
                                className="tournament-rename-input"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveRename(tournament.id);
                                  if (e.key === 'Escape') handleCancelRename();
                                }}
                                autoFocus
                              />
                              <button
                                type="button"
                                className="tournament-rename-btn rename-confirm"
                                onClick={() => handleSaveRename(tournament.id)}
                                disabled={!editingName.trim()}
                              >
                                <i className="bi bi-check"></i>
                              </button>
                              <button
                                type="button"
                                className="tournament-rename-btn rename-cancel"
                                onClick={handleCancelRename}
                              >
                                <i className="bi bi-x"></i>
                              </button>
                            </div>
                          ) : (
                            <div className="tournament-item-name">
                              {tournament.state?.config?.tournamentName || 'Sans nom'}
                              {tournament.id === firebaseSyncService.currentTournamentId && (
                                <span className="tournament-badge-current">Actuel</span>
                              )}
                            </div>
                          )}
                          <div className="tournament-item-date">
                            <i className="bi bi-clock me-1"></i>
                            Modifié: {formatDate(tournament.updatedAt)}
                          </div>
                        </div>

                        <div className="tournament-actions">
                          <button
                            type="button"
                            className="tournament-action-btn action-load"
                            onClick={() => handleLoadClick(tournament)}
                            disabled={loading}
                            title="Charger"
                          >
                            <i className="bi bi-folder-symlink"></i>
                          </button>
                          <button
                            type="button"
                            className="tournament-action-btn action-rename"
                            onClick={() => handleStartRename(tournament)}
                            disabled={loading}
                            title="Renommer"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="tournament-action-btn action-duplicate"
                            onClick={() => handleDuplicateTournament(tournament.id)}
                            disabled={loading}
                            title="Dupliquer"
                          >
                            <i className="bi bi-files"></i>
                          </button>
                          <button
                            type="button"
                            className="tournament-action-btn action-delete"
                            onClick={() => handleDeleteClick(tournament)}
                            disabled={loading}
                            title="Supprimer"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn"
                onClick={onClose}
                disabled={loading}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmModal
        isOpen={showLoadConfirm}
        onClose={() => setShowLoadConfirm(false)}
        onConfirm={() => handleLoadTournament(pendingLoadTournament?.id)}
        title="Charger un tournoi"
        message={`Charger "${pendingLoadTournament?.state?.config?.tournamentName || 'Sans nom'}" ? Le tournoi en cours sera remplacé.`}
        confirmLabel="Charger"
        confirmVariant="primary"
        icon="bi-folder-symlink"
      />
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => handleDeleteTournament(pendingDeleteTournament?.id)}
        title="Supprimer un tournoi"
        message={`Supprimer "${pendingDeleteTournament?.state?.config?.tournamentName || 'Sans nom'}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        confirmVariant="danger"
        icon="bi-trash"
      />
    </>
  );
};

export default TournamentListModal;
